/**
 * League and Team Configuration
 * 
 * This module contains all league-specific configurations including:
 * - League metadata (names, codes, overseas limits, local nationality)
 * - Team logo mappings organized by league
 */

// League configuration interface
export interface LeagueConfig {
  name: string;
  code: string;
  maxOverseas: number;
  localNationality: string;
  hasImpactSubstitute: boolean; // Whether the league allows impact substitutes
  showSalary: boolean; // Show contract prices; only where the salary table stores a consistent unit (IPL: INR crore)
}

// League configurations
export const LEAGUE_CONFIG: Record<string, LeagueConfig> = {
  IPL: {
    name: 'Indian Premier League',
    code: 'IPL',
    maxOverseas: 4,
    localNationality: 'India',
    hasImpactSubstitute: true,
    showSalary: true,
  },
  SA20: {
    name: 'SA20',
    code: 'SA20',
    maxOverseas: 4,
    localNationality: 'South Africa',
    hasImpactSubstitute: false,
    showSalary: false,
  },
  CPL: {
    name: 'Caribbean Premier League',
    code: 'CPL',
    maxOverseas: 5,
    localNationality: 'West Indies',
    hasImpactSubstitute: false,
    showSalary: false,
  },
  THE_HUNDRED: {
    name: 'The Hundred',
    code: 'THE HUNDRED',
    maxOverseas: 4,
    hasImpactSubstitute: false,
    localNationality: 'England',
    showSalary: false,
  },
};

export const DEFAULT_LEAGUE = 'IPL';

// Team logo mappings organized by league
// Keys are team short codes (uppercase), values are logo paths
export const TEAM_LOGO_MAP: Record<string, Record<string, string>> = {
  IPL: {
    'CSK': '/static/IPL/csk-logo.jpg',
    'MI': '/static/IPL/MI-logo.jpg',
    'RCB': '/static/IPL/RCB-logo.jpg',
    'KKR': '/static/IPL/kkr-logo.jpg',
    'DC': '/static/IPL/dc-logo.jpg',
    'RR': '/static/IPL/RR-logo.jpg',
    'PBKS': '/static/IPL/PBKS-logo.jpg',
    'SRH': '/static/IPL/SH-logo.jpg',
    'SH': '/static/IPL/SH-logo.jpg', // Alternative filename
    'GT': '/static/IPL/GT-logo.jpg',
    'LSG': '/static/IPL/LSG-logo.jpg',
  },
  SA20: {
    // Durban's Super Giants
    'DSG': '/static/SA20/dsg-logo.webp',
    'DURBAN': '/static/SA20/dsg-logo.webp',
    "DURBAN'S SUPER GIANTS": '/static/SA20/dsg-logo.webp',
    // Joburg Super Kings
    'JSK': '/static/SA20/jsk-logo.webp',
    'JOBURG': '/static/SA20/jsk-logo.webp',
    'JOBURG SUPER KINGS': '/static/SA20/jsk-logo.webp',
    // MI Cape Town
    'MICT': '/static/SA20/mict-logo.webp',
    'MI CAPE TOWN': '/static/SA20/mict-logo.webp',
    'MICAPETOWN': '/static/SA20/mict-logo.webp',
    // Paarl Royals
    'PR': '/static/SA20/pr.webp',
    'PAARL': '/static/SA20/pr.webp',
    'PAARL ROYALS': '/static/SA20/pr.webp',
    // Pretoria Capitals
    'PC': '/static/SA20/pc-logo.webp',
    'PRETORIA': '/static/SA20/pc-logo.webp',
    'PRETORIA CAPITALS': '/static/SA20/pc-logo.webp',
    // Sunrisers Eastern Cape
    'SEC': '/static/SA20/sec-logo.webp',
    'SUNRISERS': '/static/SA20/sec-logo.webp',
    'SUNRISERS EASTERN CAPE': '/static/SA20/sec-logo.webp',
  },
  CPL: {
    // St Lucia Kings
    'SLZ': '/static/CPL/SLZ-logo.webp',
    'ST LUCIA': '/static/CPL/SLZ-logo.webp',
    'ST LUCIA KINGS': '/static/CPL/SLZ-logo.webp',
    // St Kitts & Nevis Patriots
    'SKNP': '/static/CPL/SKNP-logo.webp',
    'ST KITTS': '/static/CPL/SKNP-logo.webp',
    'ST KITTS & NEVIS PATRIOTS': '/static/CPL/SKNP-logo.webp',
    // Antigua and Barbuda Falcons
    'ANBF': '/static/CPL/ANBF-logo.webp',
    'ANTIGUA': '/static/CPL/ANBF-logo.webp',
    'ANTIGUA AND BARBUDA FALCONS': '/static/CPL/ANBF-logo.webp',
    // Trinbago Knight Riders
    'TKR': '/static/CPL/TKR-logo.webp',
    'T&T': '/static/CPL/TKR-logo.webp',
    'TRINBAGO': '/static/CPL/TKR-logo.webp',
    'TRINBAGO KNIGHT RIDERS': '/static/CPL/TKR-logo.webp',
    // Guyana Amazon Warriors
    'GUY': '/static/CPL/GUY-logo.webp',
    'GUYANA': '/static/CPL/GUY-logo.webp',
    'GUYANA AMAZON WARRIORS': '/static/CPL/GUY-logo.webp',
    // Barbados Royals
    'BR': '/static/CPL/BR-logo.webp',
    'BAR': '/static/CPL/BR-logo.webp',
    'BARBADOS': '/static/CPL/BR-logo.webp',
    'BARBADOS ROYALS': '/static/CPL/BR-logo.webp',
  },
  THE_HUNDRED: {
    // MI London (Oval Invincibles)
    'OIM': '/static/THE_HUNDRED/mi-london.svg',
    'OI': '/static/THE_HUNDRED/mi-london.svg',
    'MI LONDON': '/static/THE_HUNDRED/mi-london.svg',
    'OVAL INVINCIBLES': '/static/THE_HUNDRED/mi-london.svg',
    // Sunrisers Leeds (Northern Superchargers)
    'NSM': '/static/THE_HUNDRED/sunrisers-leeds.svg',
    'NS': '/static/THE_HUNDRED/sunrisers-leeds.svg',
    'SUNRISERS LEEDS': '/static/THE_HUNDRED/sunrisers-leeds.svg',
    'NORTHERN SUPERCHARGERS': '/static/THE_HUNDRED/sunrisers-leeds.svg',
    // Welsh Fire
    'WFM': '/static/THE_HUNDRED/Welsh_Fire_logo.svg.png',
    'WF': '/static/THE_HUNDRED/Welsh_Fire_logo.svg.png',
    'WELSH FIRE': '/static/THE_HUNDRED/Welsh_Fire_logo.svg.png',
    // London Spirit
    'LSM': '/static/THE_HUNDRED/London_Spirit_new_logo.svg.png',
    'LS': '/static/THE_HUNDRED/London_Spirit_new_logo.svg.png',
    'LONDON SPIRIT': '/static/THE_HUNDRED/London_Spirit_new_logo.svg.png',
    // Birmingham Phoenix
    'BPM': '/static/THE_HUNDRED/birmingham-phoenix-logo.svg',
    'BP': '/static/THE_HUNDRED/birmingham-phoenix-logo.svg',
    'BIRMINGHAM PHOENIX': '/static/THE_HUNDRED/birmingham-phoenix-logo.svg',
    // Trent Rockets
    'TRM': '/static/THE_HUNDRED/trent-rockets.svg',
    'TR': '/static/THE_HUNDRED/trent-rockets.svg',
    'TRENT ROCKETS': '/static/THE_HUNDRED/trent-rockets.svg',
    // Manchester Super Giants (Manchester Originals)
    'MOM': '/static/THE_HUNDRED/manchester-super-giants.svg',
    'MO': '/static/THE_HUNDRED/manchester-super-giants.svg',
    'MANCHESTER SUPER GIANTS': '/static/THE_HUNDRED/manchester-super-giants.svg',
    'MANCHESTER ORIGINALS': '/static/THE_HUNDRED/manchester-super-giants.svg',
    // Southern Brave
    'SBM': '/static/THE_HUNDRED/southern-brave-alt.svg',
    'SB': '/static/THE_HUNDRED/southern-brave-alt.svg',
    'SOUTHERN BRAVE': '/static/THE_HUNDRED/southern-brave-alt.svg',
  },
};

/**
 * Get the logo path for a team
 * @param teamShort - Team short code (e.g., "RR", "CSK", "DSG")
 * @param league - League code (e.g., "IPL", "SA20")
 * @returns Logo path or empty string if not found
 */
export const getTeamLogo = (
  teamShort: string | undefined | null,
  league: string = DEFAULT_LEAGUE
): string => {
  if (!teamShort || typeof teamShort !== 'string') return '';
  
  try {
    const upperTeam = teamShort.toUpperCase().trim();
    const leagueLogos = TEAM_LOGO_MAP[league] || {};

    // Try exact match
    if (leagueLogos[upperTeam]) {
      return leagueLogos[upperTeam];
    }

    // Try case-insensitive match
    const matchingKey = Object.keys(leagueLogos).find(
      (key) => key.toUpperCase() === upperTeam
    );
    if (matchingKey) {
      return leagueLogos[matchingKey];
    }

    return '';
  } catch (error) {
    console.error('Error getting team logo:', error, teamShort, league);
    return '';
  }
};

/**
 * Get league configuration
 * @param league - League code
 * @returns League config or default (IPL) config
 */
export const getLeagueConfig = (league: string): LeagueConfig => {
  return LEAGUE_CONFIG[league] || LEAGUE_CONFIG[DEFAULT_LEAGUE];
};

/**
 * Get list of supported league codes
 */
export const getSupportedLeagues = (): string[] => {
  return Object.keys(LEAGUE_CONFIG);
};
